/**
 * HeroSection — MADE-256
 *
 * First visible section on the landing page.
 * Purple gradient restricted to this section only.
 * B&W technical SVG placeholder for hero image.
 * Two CTAs: Shop Now + Custom Order.
 */

import { Box, Container, Typography, Button } from '@mui/material';

/** Computed spoke endpoints for the gear/bearing SVG */
const SPOKE_ANGLES = [0, 60, 120, 180, 240, 300];

function HeroImagePlaceholder() {
  return (
    <Box
      role="img"
      aria-label="3D-printed precision component — placeholder for product photography"
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: { xs: 320, sm: 400, md: 500 },
        aspectRatio: '1 / 1',
        mx: 'auto',
        borderRadius: 3,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 60%, #0f1522 100%)',
        boxShadow: '0 24px 64px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.07)',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 400 400"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
        aria-hidden="true"
      >
        <defs>
          <pattern id="hero-grid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
          </pattern>
          <radialGradient id="hero-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(102,126,234,0.18)" />
            <stop offset="100%" stopColor="rgba(102,126,234,0)" />
          </radialGradient>
        </defs>

        {/* Grid background */}
        <rect width="400" height="400" fill="url(#hero-grid)" />
        {/* Central glow */}
        <ellipse cx="200" cy="200" rx="180" ry="180" fill="url(#hero-glow)" />

        {/* Outer guide hex */}
        <polygon
          points="200,50 305,107.5 305,222.5 200,280 95,222.5 95,107.5"
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="1"
        />

        {/* Main hex ring — primary component outline */}
        <polygon
          points="200,72 283,118 283,210 200,256 117,210 117,118"
          fill="none"
          stroke="rgba(255,255,255,0.72)"
          strokeWidth="2"
        />

        {/* Inner hex — dashed */}
        <polygon
          points="200,100 265,138 265,196 200,234 135,196 135,138"
          fill="rgba(255,255,255,0.02)"
          stroke="rgba(255,255,255,0.32)"
          strokeWidth="1.5"
          strokeDasharray="4 3"
        />

        {/* Outer bearing ring */}
        <circle cx="200" cy="167" r="46" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2" />
        {/* Inner bearing ring */}
        <circle cx="200" cy="167" r="32" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.38)" strokeWidth="1" />
        {/* Centre hub */}
        <circle cx="200" cy="167" r="11" fill="rgba(255,255,255,0.88)" />
        {/* Centre bore */}
        <circle cx="200" cy="167" r="4.5" fill="#1a1a2e" />

        {/* Radial spokes */}
        {SPOKE_ANGLES.map((angle) => {
          const rad = (angle * Math.PI) / 180;
          const x1 = (200 + 15 * Math.cos(rad)).toFixed(1);
          const y1 = (167 + 15 * Math.sin(rad)).toFixed(1);
          const x2 = (200 + 43 * Math.cos(rad)).toFixed(1);
          const y2 = (167 + 43 * Math.sin(rad)).toFixed(1);
          return (
            <line
              key={angle}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="rgba(255,255,255,0.5)"
              strokeWidth="1.5"
            />
          );
        })}

        {/* Dimension / annotation lines */}
        <line x1="50" y1="167" x2="115" y2="167" stroke="rgba(255,255,255,0.2)" strokeWidth="0.75" strokeDasharray="3 3" />
        <line x1="285" y1="167" x2="350" y2="167" stroke="rgba(255,255,255,0.2)" strokeWidth="0.75" strokeDasharray="3 3" />
        <line x1="200" y1="30" x2="200" y2="70" stroke="rgba(255,255,255,0.2)" strokeWidth="0.75" strokeDasharray="3 3" />
        <line x1="200" y1="258" x2="200" y2="302" stroke="rgba(255,255,255,0.2)" strokeWidth="0.75" strokeDasharray="3 3" />

        {/* Tick marks at dimension line ends */}
        <line x1="50" y1="163" x2="50" y2="171" stroke="rgba(255,255,255,0.35)" strokeWidth="1" />
        <line x1="350" y1="163" x2="350" y2="171" stroke="rgba(255,255,255,0.35)" strokeWidth="1" />

        {/* Title block */}
        <rect x="138" y="318" width="124" height="30" rx="3" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
        <line x1="150" y1="328" x2="250" y2="328" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
        <line x1="150" y1="337" x2="222" y2="337" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

        {/* Corner registration marks */}
        <path d="M 22 22 L 22 40 M 22 22 L 40 22" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
        <path d="M 378 22 L 378 40 M 378 22 L 360 22" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
        <path d="M 22 378 L 22 360 M 22 378 L 40 378" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
        <path d="M 378 378 L 378 360 M 378 378 L 360 378" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
      </svg>
    </Box>
  );
}

export function HeroSection() {
  return (
    <Box
      component="section"
      aria-labelledby="hero-headline"
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: { xs: 8, md: 12 },
        px: { xs: 2, md: 4 },
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: { xs: 6, md: 8 },
          }}
        >
          {/* Copy */}
          <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              id="hero-headline"
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.6rem', md: '3.75rem', lg: '4.5rem' },
                lineHeight: 1.1,
                color: 'white',
                letterSpacing: '-0.02em',
                mb: 3,
              }}
            >
              The future.
              <br />
              Made here.
            </Typography>

            <Typography
              variant="h6"
              component="p"
              sx={{
                color: 'rgba(255,255,255,0.88)',
                fontWeight: 400,
                fontSize: { xs: '1.05rem', md: '1.2rem' },
                lineHeight: 1.75,
                mb: 5,
                maxWidth: 500,
                mx: { xs: 'auto', md: 0 },
              }}
            >
              Precision 3D-printed components for marine, industrial, and everyday use —
              designed and manufactured in Barbados.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              {/* Primary CTA */}
              <Button
                component="a"
                href="/shop"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: 'white',
                  color: '#667eea',
                  fontWeight: 700,
                  fontSize: '1rem',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                  '&:hover': {
                    backgroundColor: '#f0f0ff',
                    boxShadow: '0 6px 24px rgba(0,0,0,0.24)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Shop Now
              </Button>

              {/* Secondary CTA */}
              <Button
                component="a"
                href="/shop/contact"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'rgba(255,255,255,0.75)',
                  borderWidth: 2,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'white',
                    borderWidth: 2,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Custom Order
              </Button>
            </Box>
          </Box>

          {/* Image */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <HeroImagePlaceholder />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
